import type { CustomStoreImplementationOptions, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { defineStore } from 'pinia'
import type { WritableComputedRef } from 'vue'
import { NOOP, initAliveStoreIds, kapixContext, remove } from 'kapix-components-vue3'
import dropDownRessourceComponentStoreCustom from './store.custom'

const storeName = 'dropDownRessourceComponent'
const customImplement: CustomStoreImplementationOptions = dropDownRessourceComponentStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function dropDownRessourceComponentFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        $parentPage: NOOP,
        $parentComponent: NOOP,
        // Constants,
        $constants: {},
        // Data,
        $data: {
          title: undefined as Nullable<string>,
          contentText: undefined as Nullable<string>,
          estDeplie: undefined as Nullable<boolean>
        },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      /* @ts-ignore: to allow override in your custom file */
      $title (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.title,
          set: (value) => {
            this.$data.title = value
            this.$emit('update:title', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $contentText (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.contentText,
          set: (value) => {
            this.$data.contentText = value
            this.$emit('update:contentText', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $estDeplie (): WritableComputedRef<Nullable<boolean>> {
        return computed({
          get: () => this.$data.estDeplie,
          set: (value) => {
            this.$data.estDeplie = value
            this.$emit('update:estDeplie', value)
          }
        })
      },
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      containerClick () {
        this.$estDeplie.value = true
      },
      /* @ts-ignore: to allow override in your custom file */
      containerClickWhenSelected () {
        this.$estDeplie.value = false
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return dropDownRessourceComponentFactory(storeId)()
}

export const dropDownRessourceComponentRaw = dropDownRessourceComponentFactory()
export const dropDownRessourceComponent = () => dropDownRessourceComponentRaw() as MergeCustomStore<typeof dropDownRessourceComponentStoreCustom.instance>
